import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { Bounds, Vector2 } from '@kitware/vtk.js/types';

export interface AutomaticArch {
  points: Vector2[];
  axialPosition: number;
}

interface Grid {
  bounds: Bounds;
  nx: number;
  ny: number;
  dx: number;
  dy: number;
  planes: Float32Array[];
  heights: number[];
  low: number;
  high: number;
}
interface Curve {
  x: number;
  y: number;
  width: number;
  depth: number;
  angle: number;
}
const yieldToUI = () => new Promise<void>((resolve) => setTimeout(resolve, 0));
function checkAbort(signal?: AbortSignal) {
  signal?.throwIfAborted();
}

// Sample the central LPS band, rather than assuming IJK axes are anatomical.
// Intensities from another height must not bias the central arch's threshold.
// Memory and work stay independent of the original CBCT's voxel count.
async function sampleVolume(
  image: vtkImageData,
  signal?: AbortSignal
): Promise<Grid> {
  const bounds = image.getBounds();
  const nx = Math.min(144, Math.ceil((bounds[1] - bounds[0]) / 1.25) + 1);
  const ny = Math.min(144, Math.ceil((bounds[3] - bounds[2]) / 1.25) + 1);
  const center = (bounds[4] + bounds[5]) / 2;
  const bandStart = Math.max(bounds[4], center - 6);
  const bandEnd = Math.min(bounds[5], center + 6);
  const nz = Math.max(2, Math.ceil((bandEnd - bandStart) / 1.5) + 1);
  const dx = (bounds[1] - bounds[0]) / (nx - 1);
  const dy = (bounds[3] - bounds[2]) / (ny - 1);
  const extent = image.getExtent();
  const [sx, sy] = image.getDimensions();
  const scalars = image.getPointData().getScalars().getData();
  const start = image.worldToIndex([bounds[0], bounds[2], bounds[4]]);
  const next = image.worldToIndex([bounds[0] + dx, bounds[2], bounds[4]]);
  const delta = next.map((v, i) => v - start[i]);
  const planes: Float32Array[] = [];
  const heights: number[] = [];
  const values: number[] = [];
  for (let z = 0; z < nz; z++) {
    checkAbort(signal);
    const height = bandStart + ((z + 0.5) / nz) * (bandEnd - bandStart);
    heights.push(height);
    const plane = new Float32Array(nx * ny).fill(NaN);
    for (let y = 0; y < ny; y++) {
      const p = image.worldToIndex([bounds[0], bounds[2] + y * dy, height]);
      for (let x = 0; x < nx; x++) {
        const i = Math.round(p[0] + x * delta[0]);
        const j = Math.round(p[1] + x * delta[1]);
        const k = Math.round(p[2] + x * delta[2]);
        if (
          i < extent[0] ||
          i > extent[1] ||
          j < extent[2] ||
          j > extent[3] ||
          k < extent[4] ||
          k > extent[5]
        )
          continue;
        const value = Number(
          scalars[i - extent[0] + sx * (j - extent[2] + sy * (k - extent[4]))]
        );
        plane[y * nx + x] = value;
        if ((x + y * nx) % 7 === 0 && Number.isFinite(value))
          values.push(value);
      }
    }
    planes.push(plane);
    if (z % 8 === 0) await yieldToUI();
  }
  values.sort((a, b) => a - b);
  const quantile = (q: number) =>
    values[Math.min(values.length - 1, Math.floor(values.length * q))] ?? 0;
  const high = quantile(0.995);
  const background = quantile(0.8);
  const low = background + 0.25 * (high - background);
  return { bounds, nx, ny, dx, dy, planes, heights, low, high };
}

function projection(grid: Grid, z: number) {
  const result = new Float32Array(grid.nx * grid.ny);
  const indices = grid.heights.flatMap((height, i) =>
    Math.abs(height - z) <= 6 ? [i] : []
  );
  for (let i = 0; i < result.length; i++) {
    let maximum = -Infinity;
    for (const k of indices) {
      const value = grid.planes[k][i];
      if (Number.isFinite(value)) maximum = Math.max(maximum, value);
    }
    // Percentile clipping keeps a few metal voxels from dominating the fit.
    result[i] = Math.max(
      0,
      Math.min(1, (maximum - grid.low) / (grid.high - grid.low))
    );
  }
  return result;
}
function at(grid: Grid, plane: Float32Array, x: number, y: number) {
  const i = Math.round((x - grid.bounds[0]) / grid.dx);
  const j = Math.round((y - grid.bounds[2]) / grid.dy);
  return i < 0 || i >= grid.nx || j < 0 || j >= grid.ny
    ? 0
    : plane[j * grid.nx + i];
}
function pointOnCurve(curve: Curve, t: number): Vector2 {
  const x = t * curve.width;
  const y = t * t * curve.depth;
  const cos = Math.cos(curve.angle);
  const sin = Math.sin(curve.angle);
  return [curve.x + x * cos - y * sin, curve.y + x * sin + y * cos];
}

// Fit a continuous bright U with a darker inside/outside. A bright isolated
// filling, skull ring or a small anterior segment must not win by intensity.
function scoreCurve(grid: Grid, plane: Float32Array, curve: Curve) {
  let score = 0;
  let length = 0;
  let covered = 0;
  const sectors = [0, 0, 0];
  for (let i = 0; i < 31; i++) {
    const t = i / 15 - 1;
    const [x, y] = pointOnCurve(curve, t);
    const tangent = Math.atan2(2 * curve.depth * t, curve.width) + curve.angle;
    const nx = -Math.sin(tangent);
    const ny = Math.cos(tangent);
    const center =
      (at(grid, plane, x, y) +
        at(grid, plane, x + nx * 2, y + ny * 2) +
        at(grid, plane, x - nx * 2, y - ny * 2)) /
      3;
    const surround =
      (at(grid, plane, x + nx * 9, y + ny * 9) +
        at(grid, plane, x - nx * 9, y - ny * 9)) /
      2;
    const weight = Math.hypot(curve.width, 2 * curve.depth * t) / 15;
    length += weight;
    score += weight * (center - 0.45 * surround - 0.12);
    if (center > 0.2) {
      covered++;
      sectors[Math.min(2, Math.floor(i / 11))]++;
    }
  }
  // Require evidence on both posterior sides and the incisors.
  if (covered < 20 || sectors.some((n) => n < 4)) return -Infinity;
  return score / Math.sqrt(length * 100);
}

function fitProjection(grid: Grid, plane: Float32Array) {
  const b = grid.bounds;
  const centerX = (b[0] + b[1]) / 2;
  const spanX = b[1] - b[0];
  const spanY = b[3] - b[2];
  let best: Curve | null = null;
  let score = 0.12;
  for (let x = centerX - spanX * 0.15; x <= centerX + spanX * 0.15; x += 8) {
    for (let y = b[2] + spanY * 0.12; y <= b[2] + spanY * 0.52; y += 7) {
      for (let width = 36; width <= Math.min(58, spanX * 0.44); width += 6) {
        for (let depth = 48; depth <= Math.min(80, spanY * 0.6); depth += 10) {
          if (depth < width || depth > width * 2) continue;
          for (const angle of [-0.25, 0, 0.25]) {
            const curve = { x, y, width, depth, angle };
            const value = scoreCurve(grid, plane, curve);
            if (value > score) {
              best = curve;
              score = value;
            }
          }
        }
      }
    }
  }
  if (!best) return null;
  // Coordinate refinement at progressively finer physical resolutions.
  for (const step of [4, 2, 1]) {
    for (let iteration = 0; iteration < 3; iteration++) {
      for (const key of ['x', 'y', 'width', 'depth', 'angle'] as const) {
        for (const sign of [-1, 1]) {
          const candidate: Curve = {
            ...best,
            [key]: best[key] + sign * (key === 'angle' ? step * 0.015 : step),
          };
          if (
            candidate.width < 34 ||
            candidate.width > 60 ||
            candidate.depth < 46 ||
            candidate.depth > 85
          )
            continue;
          if (
            candidate.depth < candidate.width ||
            candidate.depth > candidate.width * 2
          )
            continue;
          const value = scoreCurve(grid, plane, candidate);
          if (value > score) {
            best = candidate;
            score = value;
          }
        }
      }
    }
  }
  return { curve: best, score };
}

/** Propose an editable dental arch from a full CBCT; return null without evidence. */
export async function detectAutomaticArch(
  image: vtkImageData,
  signal?: AbortSignal
): Promise<AutomaticArch | null> {
  const b = image.getBounds();
  if (
    b[1] - b[0] < 55 ||
    b[3] - b[2] < 55 ||
    image.getPointData().getScalars()?.getNumberOfComponents() !== 1
  )
    return null;
  const grid = await sampleVolume(image, signal);
  if (!Number.isFinite(grid.low) || grid.high - grid.low < 1e-6) return null;
  // Start at the axial slider's midpoint. Do not select a stronger ridge at
  // another height (e.g. the mandibular border instead of the dentition).
  const axialPosition = (b[4] + b[5]) / 2;
  checkAbort(signal);
  const best = fitProjection(grid, projection(grid, axialPosition));
  if (!best) return null;
  const points = Array.from({ length: 13 }, (_, i) =>
    pointOnCurve(best.curve, i / 6 - 1)
  );
  if (points.some(([x, y]) => x < b[0] || x > b[1] || y < b[2] || y > b[3]))
    return null;
  return { points, axialPosition };
}
